/*
  Falcon PDP gallery.

  Slides AND thumbnails are rendered server-side by
  snippets/pdp-gallery.liquid, so the gallery is complete before this file
  runs and stays usable if it never does. This module only enhances:
    - thumbnail / arrow / swipe navigation and the active state
    - the zoom lightbox
    - swapping the media set when the variant changes

  The current item list is read from the DOM rather than from the embedded JSON,
  so a malformed or empty payload can never blank out a working gallery.

  Variant galleries: `variant.metafields` only resolves for the *selected* variant
  on this store, so a server-side map of every colour comes back empty. Other
  colours are therefore fetched from `?variant=<id>&view=variant-gallery`
  (templates/product.variant-gallery.liquid — a `layout none` JSON endpoint), the
  same approach assets/gallery-carousel.js uses, with URL normalisation
  mirrored from it and a per-key cache. The embedded JSON is still tried first, so
  nothing is fetched if the metafields ever do resolve server-side.
*/
(function () {
  'use strict';

  var SELECTORS = {
    data: 'script[data-pdp-gallery-data]',
    viewport: '[data-pdp-gallery-viewport]',
    thumbs: '[data-pdp-gallery-thumbs]',
    prev: '[data-pdp-gallery-prev]',
    next: '[data-pdp-gallery-next]',
    lightbox: '[data-pdp-gallery-lightbox]',
    lightboxFigure: '[data-pdp-gallery-lightbox-figure]',
    lightboxClose: '[data-pdp-gallery-lightbox-close]',
    variantElement: 'variant-radios, variant-selects, variant-mixed-inputs',
    scope: '[data-layout-1-pdp]'
  };

  var PLAY_ICON =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" aria-hidden="true" focusable="false">' +
    '<path d="M232.4,114.49,88.32,26.35a16,16,0,0,0-16.2-.3A15.86,15.86,0,0,0,64,39.87V216.13A15.94,15.94,0,0,0,80,232a16.07,16.07,0,0,0,8.36-2.35L232.4,141.51a15.81,15.81,0,0,0,0-27Z"></path></svg>';

  var SHOP_URL = window.location.origin;
  var IMAGE_WIDTH = 1200;
  var THUMB_WIDTH = 200;

  /* Mirrors fixShopifyUrl() in assets/gallery-carousel.js. */
  function fixShopifyUrl(url) {
    if (!url) return url;
    var out = String(url);
    if (out.indexOf('/products/files/') !== -1) out = out.replace('/products/files/', '/cdn/shop/files/');
    if (out.indexOf('//') === 0) return 'https:' + out;
    if (out.indexOf('http') === 0) return out;
    if (out.indexOf('/cdn/shop/') === 0) return SHOP_URL + out;
    if (out.indexOf('/files/') === 0) return SHOP_URL + '/cdn/shop' + out;
    if (out.charAt(0) === '/') return SHOP_URL + out;
    return SHOP_URL + '/cdn/shop/files/' + out;
  }

  function sizedUrl(url, width) {
    var fixed = fixShopifyUrl(url);
    if (!fixed) return fixed;
    if (fixed.indexOf('cdn.shopify.com') === -1 && fixed.indexOf('/cdn/shop/') === -1) return fixed;
    return fixed + (fixed.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function looksLikeVideo(value) {
    var v = String(value || '').toLowerCase();
    return v.indexOf('.mp4') !== -1 || v.indexOf('.webm') !== -1 || v.indexOf('.mov') !== -1 || v.indexOf('/videos/') !== -1;
  }

  /*
    Turns one raw `custom.variant_gallery` entry — a Shopify media object, a file
    reference exposing `.src`, or a bare URL string — into the shape render() wants.
  */
  function normaliseEntry(entry, alt) {
    if (!entry) return null;

    if (entry.media_type === 'video' || entry.type === 'video') {
      var src = null;
      if (entry.sources && entry.sources.length) {
        for (var i = 0; i < entry.sources.length; i += 1) {
          var source = entry.sources[i];
          if (source.format === 'mp4' || source.mime_type === 'video/mp4') {
            src = source.url || source.src;
            break;
          }
        }
        if (!src) src = entry.sources[0].url || entry.sources[0].src;
      } else {
        src = entry.src;
      }
      if (!src) return null;
      return {
        type: 'video',
        url: fixShopifyUrl(src),
        thumb: entry.preview_image && entry.preview_image.src ? sizedUrl(entry.preview_image.src, THUMB_WIDTH) : '',
        alt: entry.alt || alt || ''
      };
    }

    var raw = entry.src || (typeof entry === 'string' ? entry : entry.url);
    if (!raw) return null;

    if (looksLikeVideo(raw)) {
      return { type: 'video', url: fixShopifyUrl(raw), thumb: '', alt: entry.alt || alt || '' };
    }
    return {
      type: 'image',
      url: sizedUrl(raw, IMAGE_WIDTH),
      thumb: sizedUrl(raw, THUMB_WIDTH),
      alt: entry.alt || alt || ''
    };
  }

  function parseJSON(element) {
    if (!element) return null;
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('[pdp-gallery] gallery data could not be parsed; variant swapping disabled', error);
      return null;
    }
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function PdpGallery(root) {
    this.root = root;
    this.scope = root.closest(SELECTORS.scope) || document;
    this.data = parseJSON(root.querySelector(SELECTORS.data)) || {};
    this.viewport = root.querySelector(SELECTORS.viewport);
    this.thumbsEl = root.querySelector(SELECTORS.thumbs);
    this.prevEl = root.querySelector(SELECTORS.prev);
    this.nextEl = root.querySelector(SELECTORS.next);
    this.lightbox = root.querySelector(SELECTORS.lightbox);
    this.lightboxFigure = root.querySelector(SELECTORS.lightboxFigure);
    this.zoomEnabled = root.dataset.zoom === 'true' && !!this.lightbox && !!this.lightboxFigure;
    this.thumbBadge = root.dataset.thumbBadge || '';
    this.thumbBadgeCount = parseInt(root.dataset.thumbBadgeCount, 10) || 0;
    this.fitClass = this.data.fit === 'contain' ? ' fp-gallery__media--contain' : '';
    this.alt = root.dataset.alt || '';
    this.renderedKey = root.dataset.renderedKey || null;
    this.index = 0;

    if (!this.viewport) return;

    this.bind();
    this.update();
  }

  PdpGallery.prototype.slides = function () {
    return Array.prototype.slice.call(this.viewport.querySelectorAll('.fp-gallery__slide'));
  };

  /* The rendered slides are the source of truth for what is on screen. */
  PdpGallery.prototype.items = function () {
    return this.slides().map(function (slide) {
      var media = slide.querySelector('img, video');
      return {
        type: slide.dataset.type || 'image',
        url: slide.dataset.url || (media ? media.currentSrc || media.src : ''),
        alt: media && media.alt ? media.alt : '',
        mediaId: slide.dataset.mediaId || ''
      };
    });
  };

  PdpGallery.prototype.bind = function () {
    var self = this;

    if (this.thumbsEl) {
      this.thumbsEl.addEventListener('click', function (event) {
        var thumb = event.target.closest('.fp-thumb');
        if (!thumb) return;
        self.goTo(parseInt(thumb.dataset.index, 10) || 0);
      });
    }

    if (this.prevEl) {
      this.prevEl.addEventListener('click', function () {
        self.goTo(self.index - 1);
      });
    }

    if (this.nextEl) {
      this.nextEl.addEventListener('click', function () {
        self.goTo(self.index + 1);
      });
    }

    this.viewport.addEventListener(
      'scroll',
      function () {
        if (self.scrollRaf) return;
        self.scrollRaf = requestAnimationFrame(function () {
          self.scrollRaf = null;
          self.syncFromScroll();
        });
      },
      { passive: true }
    );

    this.root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        self.goTo(self.index - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        self.goTo(self.index + 1);
      }
    });

    this.escHandler = function (event) {
      if (event.key === 'Escape') self.closeLightbox();
    };

    if (this.zoomEnabled) {
      this.viewport.addEventListener('click', function (event) {
        if (event.target.closest('video')) return;
        self.openLightbox();
      });
      this.viewport.style.cursor = 'zoom-in';

      this.lightbox.addEventListener('click', function (event) {
        if (event.target.closest(SELECTORS.lightboxClose) || event.target === self.lightbox) {
          self.closeLightbox();
        }
      });
    }

    this.bindVariantChange();
  };

  PdpGallery.prototype.bindVariantChange = function () {
    var self = this;
    var handler = function (event) {
      var variant = event && event.detail ? event.detail.variant : null;
      if (variant) self.onVariantChange(variant);
    };

    var attach = function (elements) {
      Array.prototype.forEach.call(elements, function (el) {
        if (el.pdpGalleryBound) return;
        el.pdpGalleryBound = true;
        el.addEventListener('on:variant:change', handler);
      });
      return elements.length;
    };

    if (attach(this.scope.querySelectorAll(SELECTORS.variantElement))) return;

    /* The variant elements upgrade asynchronously; wait for them once. */
    var host = this.scope === document ? document.body : this.scope;
    var observer = new MutationObserver(function () {
      if (attach(host.querySelectorAll(SELECTORS.variantElement))) observer.disconnect();
    });
    observer.observe(host, { childList: true, subtree: true });
  };

  PdpGallery.prototype.onVariantChange = function (variant) {
    var self = this;
    var variants = this.data.variants || {};
    var meta = variants[String(variant.id)];
    var key = meta && meta.key != null ? String(meta.key) : String(variant.id);

    /* Already showing this set: leave the DOM and the scroll position alone. */
    if (this.renderedKey === key) return;

    /* 1. Server-side map, if variant metafields happened to resolve. */
    var galleries = this.data.galleries || {};
    if (galleries[key] && galleries[key].length) {
      this.apply(key, galleries[key]);
      return;
    }

    /* 2. Anything already fetched for this key. */
    this.cache = this.cache || {};
    if (this.cache[key]) {
      if (this.cache[key].length) this.apply(key, this.cache[key]);
      else this.applyFallback(meta);
      return;
    }

    /* 3. Fetch this variant's gallery, keeping the current images meanwhile. */
    this.fetchGallery(variant.id)
      .then(function (items) {
        self.cache[key] = items;
        /* A newer change may have landed while this was in flight. */
        if (self.currentVariantId() !== String(variant.id)) return;
        if (items.length) self.apply(key, items);
        else self.applyFallback(meta);
      })
      .catch(function () {
        if (self.currentVariantId() === String(variant.id)) self.applyFallback(meta);
      });
  };

  PdpGallery.prototype.currentVariantId = function () {
    var input = this.scope.querySelector('input[name="id"]');
    return input ? String(input.value) : null;
  };

  PdpGallery.prototype.fetchGallery = function (variantId) {
    var self = this;
    var url = window.location.pathname + '?variant=' + encodeURIComponent(variantId) + '&view=variant-gallery';

    if (this.abortController) this.abortController.abort();
    this.abortController = typeof AbortController !== 'undefined' ? new AbortController() : null;

    return fetch(url, this.abortController ? { signal: this.abortController.signal } : undefined)
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var raw = [];
        var trimmed = text.trim();
        if (trimmed.charAt(0) === '{') {
          var data = JSON.parse(trimmed);
          if (data && Array.isArray(data.gallery)) raw = data.gallery;
        }
        return raw
          .map(function (entry) {
            return normaliseEntry(entry, self.alt);
          })
          .filter(Boolean);
      });
  };

  PdpGallery.prototype.apply = function (key, items) {
    this.renderedKey = key;
    this.render(items);
    this.goTo(0, true);
  };

  /*
    No gallery for this variant. Never blank the gallery — jump to the variant's
    featured image among the slides already rendered, else restore the full
    product media set from the embedded payload.
  */
  PdpGallery.prototype.applyFallback = function (meta) {
    var mediaId = meta ? String(meta.mediaId || '') : '';

    if (mediaId) {
      var slides = this.slides();
      for (var i = 0; i < slides.length; i += 1) {
        if (slides[i].dataset.mediaId === mediaId) {
          this.goTo(i, true);
          return;
        }
      }
    }

    var fallback = this.data.default || [];
    if (!fallback.length || this.renderedKey === '__default__') return;

    this.renderedKey = '__default__';
    this.render(fallback);

    var target = 0;
    if (mediaId) {
      for (var j = 0; j < fallback.length; j += 1) {
        if (String(fallback[j].mediaId) === mediaId) {
          target = j;
          break;
        }
      }
    }
    this.goTo(target, true);
  };

  /* Replaces slides and thumbnails together. Refuses to render an empty set. */
  PdpGallery.prototype.render = function (items) {
    if (!items || !items.length) return;
    var self = this;

    this.viewport.innerHTML = items
      .map(function (item, index) {
        var media =
          item.type === 'video'
            ? '<video class="fp-gallery__media' +
              self.fitClass +
              '" ' +
              (index === 0
                ? 'src="' + escapeHTML(item.url) + '" preload="auto" autoplay'
                : 'data-src="' + escapeHTML(item.url) + '" preload="none"') +
              (item.thumb && item.thumb !== item.url ? ' poster="' + escapeHTML(item.thumb) + '"' : '') +
              ' muted loop playsinline></video>'
            : '<img class="fp-gallery__media' +
              self.fitClass +
              '" src="' +
              escapeHTML(item.url) +
              '" alt="' +
              escapeHTML(item.alt || self.alt) +
              '" ' +
              (index === 0 ? 'fetchpriority="high"' : 'loading="lazy"') +
              ' draggable="false">';

        return (
          '<div class="fp-gallery__slide" data-index="' +
          index +
          '" data-type="' +
          escapeHTML(item.type || 'image') +
          '" data-url="' +
          escapeHTML(item.url) +
          '"' +
          (item.mediaId ? ' data-media-id="' + escapeHTML(item.mediaId) + '"' : '') +
          '>' +
          media +
          '</div>'
        );
      })
      .join('');

    if (this.thumbsEl) {
      if (items.length < 2) {
        this.thumbsEl.innerHTML = '';
        this.thumbsEl.hidden = true;
      } else {
        this.thumbsEl.hidden = false;
        this.thumbsEl.innerHTML = items
          .map(function (item, index) {
            var badge =
              self.thumbBadge && index < self.thumbBadgeCount
                ? '<span class="fp-thumb__badge">' + escapeHTML(self.thumbBadge) + '</span>'
                : '';
            var play = item.type === 'video' ? '<span class="fp-thumb__play">' + PLAY_ICON + '</span>' : '';
            return (
              '<button type="button" class="fp-thumb" data-index="' +
              index +
              '" aria-current="false" aria-label="' +
              escapeHTML((item.alt || self.alt) + ' ' + (index + 1)) +
              '"><img src="' +
              escapeHTML(item.thumb || item.url) +
              '" alt="" loading="lazy" draggable="false">' +
              badge +
              play +
              '</button>'
            );
          })
          .join('');
      }
    }
  };

  PdpGallery.prototype.goTo = function (index, instant) {
    var slides = this.slides();
    if (!slides.length) return;
    this.index = Math.max(0, Math.min(index, slides.length - 1));
    this.scrollToIndex(this.index, instant ? 'auto' : 'smooth');
    this.update();
  };

  PdpGallery.prototype.scrollToIndex = function (index, behavior) {
    var slides = this.slides();
    if (!slides[index]) return;
    this.viewport.scrollTo({ left: slides[index].offsetLeft, behavior: behavior || 'smooth' });
  };

  PdpGallery.prototype.syncFromScroll = function () {
    var slides = this.slides();
    if (!slides.length) return;
    var width = this.viewport.clientWidth || 1;
    var index = Math.max(0, Math.min(Math.round(this.viewport.scrollLeft / width), slides.length - 1));
    if (index === this.index) return;
    this.index = index;
    this.update();
  };

  PdpGallery.prototype.update = function () {
    var self = this;
    var slides = this.slides();

    if (this.thumbsEl) {
      Array.prototype.forEach.call(this.thumbsEl.querySelectorAll('.fp-thumb'), function (thumb, index) {
        thumb.classList.toggle('is-active', index === self.index);
        thumb.setAttribute('aria-current', index === self.index ? 'true' : 'false');
      });
    }

    if (this.prevEl) this.prevEl.disabled = this.index <= 0;
    if (this.nextEl) this.nextEl.disabled = this.index >= slides.length - 1;

    /* Only the visible video plays; the rest get their src on demand. */
    slides.forEach(function (slide, index) {
      var video = slide.querySelector('video');
      if (!video) return;
      if (index === self.index) {
        if (!video.getAttribute('src') && video.dataset.src) video.setAttribute('src', video.dataset.src);
        var playback = video.play();
        if (playback && typeof playback.catch === 'function') playback.catch(function () {});
      } else if (!video.paused) {
        video.pause();
      }
    });
  };

  PdpGallery.prototype.openLightbox = function () {
    if (!this.zoomEnabled) return;
    var item = this.items()[this.index];
    if (!item || !item.url) return;

    this.lightboxFigure.innerHTML =
      item.type === 'video'
        ? '<video class="fp-lightbox__media" src="' +
          escapeHTML(item.url) +
          '" controls autoplay muted loop playsinline></video>'
        : '<img class="fp-lightbox__media" src="' +
          escapeHTML(item.url) +
          '" alt="' +
          escapeHTML(item.alt || this.alt) +
          '">';

    this.lightbox.hidden = false;
    this.lightbox.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', this.escHandler);
  };

  PdpGallery.prototype.closeLightbox = function () {
    if (!this.lightbox) return;
    this.lightbox.classList.remove('is-open');
    this.lightbox.hidden = true;
    if (this.lightboxFigure) this.lightboxFigure.innerHTML = '';
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this.escHandler);
  };

  if (typeof customElements !== 'undefined' && !customElements.get('pdp-gallery')) {
    customElements.define(
      'pdp-gallery',
      class extends HTMLElement {
        connectedCallback() {
          if (this.pdpGallery) return;
          try {
            this.pdpGallery = new PdpGallery(this);
          } catch (error) {
            console.error('[pdp-gallery] failed to initialise', error);
          }
        }

        disconnectedCallback() {
          if (this.pdpGallery) this.pdpGallery.closeLightbox();
        }
      }
    );
  }
})();
